import React, { useEffect, useMemo, useState } from 'react';

import { SelectionDropdown } from 'shared/components/dropdown/SelectionDropdown';
import * as Styled from 'settings/styles/TimeSelector.style';
import { normalizeTimeToHHMM } from 'settings/utils/timeFormat';

type Meridiem = 'AM' | 'PM';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function parseHHMM(hhmm: string): { hh: number; mm: number } | null {
  const match = hhmm.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return { hh, mm };
}

function partsToHHMM(parts: {
  hour: string;
  minute: string;
  meridiem: Meridiem;
}): string | null {
  const hh12 = Number(parts.hour);
  const mm = Number(parts.minute);
  if (!Number.isFinite(hh12) || !Number.isFinite(mm)) return null;
  if (hh12 < 1 || hh12 > 12 || mm < 0 || mm > 59) return null;

  const hh24 = parts.meridiem === 'AM' ? hh12 % 12 : (hh12 % 12) + 12;
  return `${pad2(hh24)}:${pad2(mm)}`;
}

interface TimeSelectorProps {
  ariaLabel: string;
  value: string;
  onChange: (nextHHMM: string) => void | Promise<void>;
}

type TimeOption = {
  hour: number; // 1..12
  minute: string; // "00" | "15" | "30" | "45"
  label: string; // "h:mm"
};

const MINUTES = ['00', '15', '30', '45'] as const;

const TIME_OPTIONS: TimeOption[] = (() => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  return hours.flatMap((h) =>
    MINUTES.map((m) => ({ hour: h, minute: m, label: `${h}:${m}` }))
  );
})();

const MERIDIEM_OPTIONS: Meridiem[] = ['AM', 'PM'];

export default function TimeSelector({
  ariaLabel,
  value,
  onChange,
}: TimeSelectorProps): JSX.Element {
  const normalizedValue = useMemo(() => normalizeTimeToHHMM(value), [value]);
  const [time, setTime] = useState<string>(() => normalizedValue ?? '01:00');
  const [timeOpen, setTimeOpen] = useState(false);
  const [meridiemOpen, setMeridiemOpen] = useState(false);

  useEffect(() => {
    setTime(normalizedValue ?? '01:00');
  }, [normalizedValue]);

  const { hour12, minute, meridiem, selectedLabel } = useMemo(() => {
    const parsed = parseHHMM(time) ?? { hh: 1, mm: 0 };
    const m: Meridiem = parsed.hh >= 12 ? 'PM' : 'AM';
    const h12 = parsed.hh % 12 === 0 ? 12 : parsed.hh % 12;
    const minuteStr = pad2(parsed.mm);

    return {
      hour12: h12,
      minute: minuteStr,
      meridiem: m,
      selectedLabel: `${h12}:${minuteStr}`,
    };
  }, [time]);

  const selectTimeLabel = (label: string) => {
    const found = TIME_OPTIONS.find((o) => o.label === label);
    if (!found) return;
    const hhmm = partsToHHMM({
      hour: String(found.hour),
      minute: found.minute,
      meridiem,
    });
    if (!hhmm) return;
    setTime(hhmm);
    onChange(hhmm);
    setTimeOpen(false);
  };

  const selectMeridiem = (next: Meridiem) => {
    const hhmm = partsToHHMM({
      hour: String(hour12),
      minute,
      meridiem: next,
    });
    if (!hhmm) return;
    setTime(hhmm);
    onChange(hhmm);
    setMeridiemOpen(false);
  };

  const timeDropdownOptions = useMemo(
    () =>
      TIME_OPTIONS.map((opt) => ({
        value: opt.label,
        label: opt.label,
      })),
    []
  );

  const meridiemDropdownOptions = useMemo(
    () =>
      MERIDIEM_OPTIONS.map((opt) => ({
        value: opt,
        label: opt,
      })),
    []
  );

  return (
    <Styled.TimeInputsRow>
      <Styled.TimeDropdownWrapper $popoverMinWidth={260}>
        <SelectionDropdown
          ariaLabel={ariaLabel}
          value={selectedLabel}
          options={timeDropdownOptions}
          onChange={(label) => selectTimeLabel(label)}
          isOpen={timeOpen}
          onOpenChange={(next) => {
            if (next) setMeridiemOpen(false);
            setTimeOpen(next);
          }}
          fullWidth
        />
      </Styled.TimeDropdownWrapper>

      <Styled.TimeDropdownWrapper $popoverMinWidth={180}>
        <SelectionDropdown
          ariaLabel={`${ariaLabel} meridiem`}
          value={meridiem}
          options={meridiemDropdownOptions}
          onChange={(next) => selectMeridiem(next as Meridiem)}
          isOpen={meridiemOpen}
          onOpenChange={(next) => {
            if (next) setTimeOpen(false);
            setMeridiemOpen(next);
          }}
          fullWidth
        />
      </Styled.TimeDropdownWrapper>
    </Styled.TimeInputsRow>
  );
}
